import { useState } from "react";

type Counts = Record<string, number | undefined>;

/**
 * Keeps the last defined value of each count while its source reloads, so tab counts
 * never blink to empty when a toggle swaps where a number comes from. Changing `keyParts`
 * (the filters the counts were computed under) starts fresh instead of holding stale numbers.
 */
export function useHeldCounts<T extends Counts>(raw: T, keyParts: readonly unknown[]): T {
  const key = JSON.stringify(keyParts);
  const [held, setHeld] = useState<{ key: string; values: T }>({ key, values: raw });
  let next: T;
  if (held.key === key) {
    next = { ...raw };
    for (const name of Object.keys(raw) as (keyof T)[]) {
      if (next[name] === undefined) next[name] = held.values[name];
    }
  } else {
    next = raw;
  }
  const changed = held.key !== key || Object.keys(next).some((name) => next[name] !== held.values[name]);
  if (changed) setHeld({ key, values: next });
  return next;
}
